import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { BASE_URL, AUTH } from "../../config/axios";

// ─── Helpers ────────────────────────────────────────────────────────────────

// Mirrors the exact status logic used in WorkstatsToggle's getStatusColor:
// - if ANY record is "pending" → Pending (red)
// - else if ANY record is "completed" → Completed (done, excluded from EOT)
// - else → In Progress (default/fallback — also covers workstages with NO
//   records yet, since an empty array has neither "pending" nor "completed",
//   exactly like the original component)
const getWorkstageStatus = (ws) => {
  const type = ws.workstage_inputtype;
  let records = [];
  if (type === "Submission") records = ws.submission_data || [];
  if (type === "Input") records = ws.normal_input || [];
  if (type === "Stage") records = ws.stage || [];

  const statuses = records.map((r) => (r.status || "").toLowerCase().trim());

  if (statuses.includes("pending")) return "Pending";
  if (statuses.includes("completed")) return "Completed";
  return "In Progress";
};

const getPendingWorkstages = (allData) => {
  if (!allData?.workstat) return [];
  return allData.workstat
    // Only filter by the "attached to this section" list if it's actually
    // present on this allData shape (it is in WorkstatsToggle, may not be here)
    .filter((ws) => (Array.isArray(allData.workstages) ? allData.workstages.includes(ws.id) : true))
    .map((ws) => ({ ...ws, _eotStatus: getWorkstageStatus(ws) }))
    .filter((ws) => ws._eotStatus !== "Completed"); // show only actionable (Pending / In Progress) items
};

const areAllStagesCompleted = (allData) => {
  if (!allData?.workstat) return false;
  const attachedWorkstages = allData.workstat.filter((ws) =>
    Array.isArray(allData.workstages) ? allData.workstages.includes(ws.id) : true
  );
  if (attachedWorkstages.length === 0) return false; // nothing assigned yet ≠ "completed"
  return attachedWorkstages.every((ws) => getWorkstageStatus(ws) === "Completed");
};

const calcDaysDelay = (originalDate, extensionDate) => {
  if (!originalDate || !extensionDate) return "-";
  const orig = new Date(originalDate);
  const ext = new Date(extensionDate);
  const diff = Math.round((ext - orig) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const addDays = (dateStr, days) => {
  if (!dateStr || days === undefined || days === null || days === "") return null;
  const d = new Date(dateStr);
  d.setDate(d.getDate() + Number(days));
  return d.toISOString().split("T")[0];
};

// ── Fix 3: get latest EOT by max extension_upto_date, not array position ──
const getLatestEOT = (eots) => {
  if (!eots.length) return null;
  return eots.reduce((latest, eot) => {
    if (!latest) return eot;
    return new Date(eot.extension_upto_date) > new Date(latest.extension_upto_date)
      ? eot
      : latest;
  }, null);
};

// ─── Shared style tokens ──────────────────────────────────────────────────
const S = {
  blue: "#2576bc",
  bg: "#f5f7fa",
  border: "0.5px solid #e0e0e0",
  radius: "6px",
  text: "#1e293b",
  subtext: "#64748b",
  label: "#374151",
  inputBg: "#f9fafb",
  red: "#dc2626",
  green: "#16a34a",
  orange: "#d97706",
};

const inputStyle = (hasError) => ({
  width: "100%",
  padding: "8px 10px",
  border: hasError ? `1px solid ${S.red}` : S.border,
  borderRadius: "5px",
  fontSize: "13px",
  color: S.text,
  background: S.inputBg,
  outline: "none",
  boxSizing: "border-box",
});

const labelStyle = {
  fontSize: "12px",
  fontWeight: 500,
  color: S.label,
  display: "block",
  marginBottom: "6px",
};

const fieldWrap = { marginBottom: "14px" };

const errMsg = (msg) =>
  msg ? (
    <span style={{ display: "block", marginTop: "4px", fontSize: "11px", color: S.red }}>
      {msg}
    </span>
  ) : null;

// Badge styling for a workstage's status (Pending / In Progress)
const statusBadgeStyle = (status) => {
  if (status === "In Progress") {
    return { color: "#b45309", background: "#fef3c7" };
  }
  // default / "Pending"
  return { color: "#dc2626", background: "#fee2e2" };
};

// ─── Add / Edit EOT Modal ─────────────────────────────────────────────────
const AddEOTModal = ({
  show,
  onHide,
  onSave,
  editData,
  pendingWorkstages,
  loaDate,
  originalCompletionDate,
  eotCount,
  latestEOT,              // ← new prop
}) => {
  const isEdit = !!editData;
  const todayStr = new Date().toISOString().split("T")[0];
  // ── Chain logic: if previous EOT exists, use its extension date as base ──
  const baseCompletionDate = latestEOT?.extension_upto_date || originalCompletionDate;

  const buildEmptyForm = () => ({
    applied_date: "",
    applied_letter_no: "",
    applied_letter: null,
    approval_date: "",
    approval_letter_no: "",
    approval_letter: null,
    extension_upto_date: "",
    reason: "",
    stage_revisions: pendingWorkstages.map((ws) => ({
      workstage_id: ws.id,
      workstage_name: ws.name,
      status: ws._eotStatus || "Pending",
      original_completion_date: getWorkstageOriginalDate(ws), // ✅ CHANGED — was baseCompletionDate
      revised_completion_date: "",
    })),
  });
  const getWorkstageOriginalDate = (ws) => {
    const priorRevision = latestEOT?.stage_revisions?.find(
      (r) => (r.workstage_id || r.workstage) === ws.id
    );
    if (priorRevision?.revised_completion_date) {
      return priorRevision.revised_completion_date;
    }

    const calculated = addDays(loaDate, ws.duration_days);
    if (calculated) return calculated;

    return originalCompletionDate || "";
  };

  const [form, setForm] = useState(buildEmptyForm());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) return;
    setErrors({});
    if (isEdit) {
      setForm({
        ...editData,
        stage_revisions: editData.stage_revisions?.length
          ? editData.stage_revisions
          : pendingWorkstages.map((ws) => ({
            workstage_id: ws.id,
            workstage_name: ws.name,
            status: ws._eotStatus || "Pending",
            original_completion_date: getWorkstageOriginalDate(ws),
            revised_completion_date: "",
          })),
      });
    } else {
      setForm(buildEmptyForm());
    }
  }, [show]);

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => { const n = { ...p }; delete n[key]; return n; });
  };

  const setRevision = (idx, key, val) => {
    setForm((p) => {
      const revs = [...p.stage_revisions];
      revs[idx] = { ...revs[idx], [key]: val };
      return { ...p, stage_revisions: revs };
    });
    setErrors((p) => { const n = { ...p }; delete n[`rev_${idx}`]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.applied_date) e.applied_date = "Applied date is required";
    else if (form.applied_date > todayStr) e.applied_date = "Applied date cannot be in the future";
    if (!form.applied_letter_no?.trim()) e.applied_letter_no = "Letter no. is required";
    if (!form.extension_upto_date) e.extension_upto_date = "Extension date is required";
    if (!form.reason?.trim()) e.reason = "Reason is required";
    // ── Validate against baseCompletionDate (chains correctly) ──
    if (
      form.extension_upto_date &&
      baseCompletionDate &&
      new Date(form.extension_upto_date) <= new Date(baseCompletionDate)
    ) {
      e.extension_upto_date = latestEOT
        ? `Extension date must be after EOT #${latestEOT.eot_no} date (${formatDate(baseCompletionDate)})`
        : "Extension date must be after original completion date";
    }
    form.stage_revisions.forEach((r, i) => {
      if (!r.revised_completion_date) e[`rev_${i}`] = "Revised date is required";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      ...form,
      id: isEdit ? editData.id : uuidv4(),
      eot_no: isEdit ? editData.eot_no : eotCount + 1,
    };
    onSave(payload);
    onHide();
  };

  const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" };

  return (
    <Modal show={show} onHide={onHide} dialogClassName="request-leave" size="lg">
      <Modal.Header closeButton style={{ padding: "14px 20px", borderBottom: S.border, background: S.bg }}>
        <Modal.Title style={{ fontSize: "14px", fontWeight: 600, color: S.text }}>
          {isEdit ? `Edit EOT #${editData?.eot_no}` : "Add New EOT"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "16px 20px", overflowX: "hidden" }}>
        <div style={{ border: S.border, borderRadius: S.radius, overflow: "hidden", marginBottom: "14px" }}>
          <div style={{ padding: "10px 14px", background: S.bg, borderBottom: S.border, fontSize: "13px", fontWeight: 600, color: S.text }}>
            EOT Details
          </div>
          <div style={{ padding: "14px 16px" }}>
            <div style={fieldWrap}>
              <label style={labelStyle}>EOT No. <span style={{ color: S.red }}>(Auto)</span></label>
              <input
                style={{ ...inputStyle(false), background: "#f1f5f9", cursor: "not-allowed" }}
                readOnly
                value={isEdit ? editData?.eot_no : eotCount + 1}
              />
            </div>

            <div style={grid2}>
              <div style={fieldWrap}>
                <label style={labelStyle}>EOT Applied Date <span style={{ color: S.red }}>*</span></label>
                <input type="date" style={inputStyle(!!errors.applied_date)} value={form.applied_date} min={loaDate || ""} max={todayStr} onChange={(e) => set("applied_date", e.target.value)} />                {errMsg(errors.applied_date)}
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Applied Letter No. <span style={{ color: S.red }}>*</span></label>
                <input type="text" placeholder="e.g. EOT/2026/01" style={inputStyle(!!errors.applied_letter_no)} value={form.applied_letter_no} onChange={(e) => set("applied_letter_no", e.target.value)} />
                {errMsg(errors.applied_letter_no)}
              </div>
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Applied Letter (Document)</label>
              <input type="file" style={inputStyle(false)} onChange={(e) => set("applied_letter", e.target.files[0] || null)} />
            </div>

            <div style={grid2}>
              <div style={fieldWrap}>
                <label style={labelStyle}>Approval Date</label>
                <input type="date" style={inputStyle(false)} value={form.approval_date} min={form.applied_date || loaDate || ""} onChange={(e) => set("approval_date", e.target.value)} />
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Approval Letter No.</label>
                <input type="text" placeholder="e.g. APRDC/EOT/2026/01" style={inputStyle(false)} value={form.approval_letter_no} onChange={(e) => set("approval_letter_no", e.target.value)} />
              </div>
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Approval Letter (Document)</label>
              <input type="file" style={inputStyle(false)} onChange={(e) => set("approval_letter", e.target.files[0] || null)} />
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Extension of Time Granted Upto <span style={{ color: S.red }}>*</span></label>
              <input type="date" style={inputStyle(!!errors.extension_upto_date)} value={form.extension_upto_date} min={baseCompletionDate || ""} onChange={(e) => set("extension_upto_date", e.target.value)} />
              {errMsg(errors.extension_upto_date)}
              {form.extension_upto_date && originalCompletionDate && (
                <span style={{ fontSize: "11px", color: S.blue, marginTop: "4px", display: "block" }}>
                  Extension: +{calcDaysDelay(originalCompletionDate, form.extension_upto_date)} days beyond original completion date
                </span>
              )}
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Reason for EOT / Delays <span style={{ color: S.red }}>*</span></label>
              <textarea rows={3} placeholder="Describe the reason for extension..." style={{ ...inputStyle(!!errors.reason), resize: "vertical", minHeight: "80px" }} value={form.reason} onChange={(e) => set("reason", e.target.value)} />
              {errMsg(errors.reason)}
            </div>
          </div>
        </div>

        <div style={{ border: S.border, borderRadius: S.radius, overflow: "hidden", marginBottom: "14px" }}>
          <div style={{ padding: "10px 14px", background: S.bg, borderBottom: S.border, fontSize: "13px", fontWeight: 600, color: S.text }}>
            Pending &amp; In-Progress Workstage — Revised Completion Dates
          </div>
          <div style={{ padding: "14px 16px" }}>
            {form.stage_revisions.length === 0 ? (
              <p style={{ fontSize: "12px", color: S.subtext, margin: 0 }}>No pending or in-progress workstages found for this project.</p>
            ) : (
              form.stage_revisions.map((rev, idx) => {
                const badge = statusBadgeStyle(rev.status);
                return (
                  <div key={rev.workstage_id} style={{ border: S.border, borderRadius: S.radius, overflow: "hidden", marginBottom: "10px" }}>
                    <div style={{ padding: "8px 12px", background: S.bg, borderBottom: S.border, display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "#eff6ff", color: S.blue, fontSize: "10px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {idx + 1}
                      </div>
                      <span style={{ fontSize: "12px", fontWeight: 600, color: S.text }}>{rev.workstage_name}</span>
                      <span style={{ marginLeft: "auto", fontSize: "10px", fontWeight: 600, color: badge.color, background: badge.background, padding: "2px 8px", borderRadius: "20px" }}>
                        {rev.status || "Pending"}
                      </span>
                    </div>
                    <div style={{ padding: "12px 14px" }}>
                      <div style={grid2}>
                        <div style={fieldWrap}>
                          <label style={labelStyle}>Original Completion Date <span style={{ color: S.red }}>(Read-Only)</span></label>
                          <input readOnly style={{ ...inputStyle(false), background: "#f1f5f9", cursor: "not-allowed" }} value={rev.original_completion_date || originalCompletionDate || ""} />
                        </div>
                        <div style={fieldWrap}>
                          <label style={labelStyle}>Revised Completion Date <span style={{ color: S.red }}>*</span></label>
                          <input type="date" style={inputStyle(!!errors[`rev_${idx}`])} value={rev.revised_completion_date} min={originalCompletionDate || ""} onChange={(e) => setRevision(idx, "revised_completion_date", e.target.value)} />
                          {errMsg(errors[`rev_${idx}`])}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "12px", borderTop: S.border }}>
          <button onClick={onHide} style={{ padding: "7px 18px", borderRadius: "5px", border: S.border, background: "#fff", color: S.subtext, fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} style={{ padding: "7px 18px", borderRadius: "5px", border: "none", background: S.blue, color: "#fff", fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>
            {isEdit ? "Update EOT" : "Save EOT"}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

// ─── EOT History Modal ────────────────────────────────────────────────────
const EOTHistoryModal = ({ show, onHide, eots, projectData }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const latestEOT = getLatestEOT(eots);

  return (
    <Modal show={show} onHide={onHide} dialogClassName="request-leave" size="lg">
      <Modal.Header closeButton style={{ padding: "14px 20px", borderBottom: S.border, background: S.bg }}>
        <Modal.Title style={{ fontSize: "14px", fontWeight: 600, color: S.text }}>
          History of EOTs — {projectData?.project_details_data?.pr_code}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "16px 20px", overflowX: "hidden" }}>
        {eots.length === 0 ? (
          <p style={{ fontSize: "13px", color: S.subtext, textAlign: "center", padding: "24px 0" }}>No EOT history available.</p>
        ) : (
          eots.map((eot, idx) => {
            const isLatest = latestEOT?.id === eot.id;
            return (
              <div key={eot.id} style={{ border: isLatest ? `1px solid ${S.green}` : S.border, borderRadius: S.radius, overflow: "hidden", marginBottom: "10px" }}>
                <div
                  onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                  style={{ display: "flex", alignItems: "center", padding: "10px 14px", background: isLatest ? "#f0fdf4" : S.bg, borderBottom: openIdx === idx ? S.border : "none", cursor: "pointer", gap: "10px" }}
                >
                  <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: isLatest ? "#dcfce7" : "#eff6ff", color: isLatest ? S.green : S.blue, fontSize: "11px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {eot.eot_no}
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: S.text, flex: 1 }}>
                    EOT #{eot.eot_no} — Applied: {formatDate(eot.applied_date)}
                  </span>
                  {/* ── Fix 2: superseded badge ── */}
                  {isLatest ? (
                    <span style={{ fontSize: "10px", fontWeight: 700, color: S.green, background: "#dcfce7", padding: "2px 8px", borderRadius: "20px" }}>
                      Active
                    </span>
                  ) : (
                    <span style={{ fontSize: "10px", fontWeight: 700, color: "#6b7280", background: "#f3f4f6", padding: "2px 8px", borderRadius: "20px" }}>
                      Superseded
                    </span>
                  )}
                  <span style={{ fontSize: "12px", color: S.subtext }}>Extended upto: {formatDate(eot.extension_upto_date)}</span>
                  <span style={{ fontSize: "12px", color: S.subtext, marginLeft: "8px" }}>{openIdx === idx ? "▲" : "▼"}</span>
                </div>

                {openIdx === idx && (
                  <div style={{ padding: "14px 16px", background: "#fff" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", marginBottom: "12px" }}>
                      {[
                        ["Applied Letter No.", eot.applied_letter_no || "-"],
                        ["Approval Date", formatDate(eot.approval_date)],
                        ["Approval Letter No.", eot.approval_letter_no || "-"],
                        ["Days Extended", `+${calcDaysDelay(projectData?.project_details_data?.completion_date, eot.extension_upto_date)} days`],
                      ].map(([label, val]) => (
                        <div key={label}>
                          <p style={{ fontSize: "11px", color: "#888", margin: "0 0 2px" }}>{label}</p>
                          <p style={{ fontSize: "13px", fontWeight: 500, color: S.text, margin: 0 }}>{val}</p>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginBottom: "12px" }}>
                      <p style={{ fontSize: "11px", color: "#888", margin: "0 0 2px" }}>Reason</p>
                      <p style={{ fontSize: "13px", color: S.text, margin: 0, lineHeight: 1.6 }}>{eot.reason || "-"}</p>
                    </div>

                    {eot.stage_revisions?.length > 0 && (
                      <>
                        <p style={{ fontSize: "11px", color: "#888", margin: "0 0 6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Stage Revisions
                        </p>
                        <div style={{ border: S.border, borderRadius: S.radius, overflow: "hidden" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                            <thead>
                              <tr style={{ background: S.bg }}>
                                {["Workstage", "Original Date", "Revised Date"].map((h) => (
                                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: S.subtext, borderBottom: S.border }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {eot.stage_revisions.map((r, ri) => (
                                <tr key={ri} style={{ borderBottom: ri < eot.stage_revisions.length - 1 ? S.border : "none" }}>
                                  <td style={{ padding: "8px 12px", color: S.text }}>{r.workstage_name}</td>
                                  <td style={{ padding: "8px 12px", color: S.subtext }}>{formatDate(r.original_completion_date)}</td>
                                  <td style={{ padding: "8px 12px", color: S.green, fontWeight: 500 }}>{formatDate(r.revised_completion_date)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                      {eot.applied_letter && (
                        <a href={typeof eot.applied_letter === "string" ? eot.applied_letter : URL.createObjectURL(eot.applied_letter)} target="_blank" rel="noopener noreferrer" style={{ fontSize: "12px", color: S.blue }}>
                          📄 Applied Letter
                        </a>
                      )}
                      {eot.approval_letter && (
                        <a href={typeof eot.approval_letter === "string" ? eot.approval_letter : URL.createObjectURL(eot.approval_letter)} target="_blank" rel="noopener noreferrer" style={{ fontSize: "12px", color: S.blue }}>
                          📄 Approval Letter
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </Modal.Body>
    </Modal>
  );
};

// ─── Delete Confirm Modal — Fix 1: shows correct EOT details ─────────────
const DeleteConfirmModal = ({ show, onHide, onConfirm, deleteTarget }) => (
  <Modal show={show} onHide={onHide} size="sm" centered>
    <Modal.Header closeButton style={{ padding: "14px 20px", borderBottom: S.border, background: S.bg }}>
      <Modal.Title style={{ fontSize: "13px", fontWeight: 600, color: S.text }}>
        Remove EOT #{deleteTarget?.eot_no}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ padding: "16px 20px" }}>
      <p style={{ fontSize: "13px", color: S.text, margin: "0 0 10px" }}>
        Are you sure you want to remove this EOT record? This action cannot be undone.
      </p>
      {deleteTarget && (
        <div style={{ background: "#fff5f5", border: "0.5px solid #fca5a5", borderRadius: S.radius, padding: "10px 12px" }}>
          <p style={{ fontSize: "11px", color: "#7f1d1d", margin: "0 0 4px", fontWeight: 600 }}>You are deleting:</p>
          <p style={{ fontSize: "12px", color: "#991b1b", margin: "0 0 2px" }}>EOT #{deleteTarget.eot_no} — {formatDate(deleteTarget.applied_date)}</p>
          <p style={{ fontSize: "12px", color: "#991b1b", margin: 0 }}>Extension upto: {formatDate(deleteTarget.extension_upto_date)}</p>
        </div>
      )}
    </Modal.Body>
    <Modal.Footer style={{ padding: "10px 20px", borderTop: S.border, background: S.bg }}>
      <button onClick={onHide} style={{ padding: "6px 16px", borderRadius: "5px", border: S.border, background: "#fff", color: S.subtext, fontSize: "12px", cursor: "pointer" }}>Cancel</button>
      <button onClick={onConfirm} style={{ padding: "6px 16px", borderRadius: "5px", border: "none", background: "#dc2626", color: "#fff", fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>
        Remove EOT #{deleteTarget?.eot_no}
      </button>
    </Modal.Footer>
  </Modal>
);

// ─── Main EOT Timeline Tab ────────────────────────────────────────────────
const EOTTimeline = ({ allData, userRole = "admin" }) => {
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const isSuperAdmin = userRole === "superadmin";

  const projectData = allData?.project_details_data || {};
  const loaDate = projectData.loa_date || "";
  const originalCompletionDate = projectData.completion_date || "";
  const pendingWorkstages = getPendingWorkstages(allData);
  const allStagesCompleted = areAllStagesCompleted(allData);

  const [eots, setEots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Fix 3: use max extension_upto_date, not array position ───────────
  const latestEOT = getLatestEOT(eots);
  const latestExtDate = latestEOT?.extension_upto_date || null;
  const totalDaysDelay = calcDaysDelay(originalCompletionDate, latestExtDate);

  const projectDetailsId = allData?.id;

  // ── GET ───────────────────────────────────────────────────────────────
  const fetchEOTs = async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/dpr/eot/list/${id}/`, AUTH());
      const sorted = [...(res.data || [])].sort((a, b) => a.eot_no - b.eot_no);
      setEots(sorted);
    } catch (err) {
      console.error("Failed to fetch EOTs:", err);
      toast.error("Failed to load EOT data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectDetailsId) return;
    fetchEOTs(projectDetailsId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetailsId]);

  // ── Expiry reminder ───────────────────────────────────────────────────
  useEffect(() => {
    if (!eots.length) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Only check the active (latest) EOT for reminders
    if (!latestEOT) return;
    const eotDate = new Date(latestEOT.extension_upto_date);
    eotDate.setHours(0, 0, 0, 0);
    const daysLeft = Math.round((eotDate - today) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) {
      toast.error(`⚠️ EOT #${latestEOT.eot_no} expired on ${formatDate(latestEOT.extension_upto_date)}. Please raise a new EOT.`, { autoClose: 8000 });
    } else if (daysLeft <= 3) {
      toast.warning(`⏰ EOT #${latestEOT.eot_no} expiring in ${daysLeft === 0 ? "today" : `${daysLeft} day${daysLeft > 1 ? "s" : ""}`} (${formatDate(latestEOT.extension_upto_date)}). Please take action.`, { autoClose: 10000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eots.length]);

  // ── POST ──────────────────────────────────────────────────────────────
  const handleCreate = async (payload) => {
    const loadingId = toast.loading("Saving EOT...");
    try {
      const fd = buildFormData(payload);
      const res = await axios.post(`${BASE_URL}/dpr/eot/create/`, fd, {
        headers: { ...AUTH().headers, "Content-Type": "multipart/form-data" },
      });
      if (res.status === 200 || res.status === 201) {
        toast.success(`EOT #${res.data.eot_no || payload.eot_no} added successfully`);
        fetchEOTs(projectDetailsId);
      }
    } catch (err) {
      console.error("EOT create failed:", err);
      toast.error("Failed to save EOT. Please try again.");
    } finally {
      toast.dismiss(loadingId);
    }
  };

  // ── PUT ───────────────────────────────────────────────────────────────
  const handleUpdate = async (payload) => {
    const loadingId = toast.loading("Updating EOT...");
    try {
      const fd = buildFormData(payload);
      const res = await axios.put(`${BASE_URL}/dpr/eot/update/${payload.id}/`, fd, {
        headers: { ...AUTH().headers, "Content-Type": "multipart/form-data" },
      });
      if (res.status === 200 || res.status === 201) {
        toast.success(`EOT #${payload.eot_no} updated successfully`);
        fetchEOTs(projectDetailsId);
        setEditTarget(null);
      }
    } catch (err) {
      console.error("EOT update failed:", err);
      toast.error("Failed to update EOT. Please try again.");
    } finally {
      toast.dismiss(loadingId);
    }
  };

  const handleSave = (payload) => {
    if (editTarget) {
      handleUpdate(payload);
    } else {
      handleCreate(payload);
    }
  };

  // ── DELETE ────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const loadingId = toast.loading("Removing EOT...");
    try {
      await axios.delete(`${BASE_URL}/dpr/eot/delete/${deleteTarget.id}/`, AUTH());
      toast.success(`EOT #${deleteTarget.eot_no} removed`);
      setDeleteTarget(null);
      fetchEOTs(projectDetailsId);
    } catch (err) {
      console.error("EOT delete failed:", err);
      toast.error("Failed to remove EOT.");
    } finally {
      toast.dismiss(loadingId);
    }
  };

  // ── FormData builder ──────────────────────────────────────────────────
  const buildFormData = (payload) => {
    const fd = new FormData();
    fd.append("project_filter", allData?.id);
    fd.append("applied_date", payload.applied_date || "");
    fd.append("applied_letter_no", payload.applied_letter_no || "");
    if (payload.approval_date) {
      fd.append("approval_date", payload.approval_date);
    }
    fd.append("approval_letter_no", payload.approval_letter_no || "");
    fd.append("extension_upto_date", payload.extension_upto_date || "");
    fd.append("reason", payload.reason || "");
    if (payload.applied_letter instanceof File) fd.append("applied_letter", payload.applied_letter);
    if (payload.approval_letter instanceof File) fd.append("approval_letter", payload.approval_letter);
    const normalizedRevisions = (payload.stage_revisions || []).map((r) => ({
      ...r,
      workstage_id: r.workstage_id || r.workstage,
    }));
    fd.append("stage_revisions", JSON.stringify(normalizedRevisions));
    return fd;
  };

  // ── Fix 2: superseded badge in table ─────────────────────────────────
  const isActiveEOT = (eot) => latestEOT?.id === eot.id;

  const chips = [
    { label: "LOA Date", value: formatDate(loaDate), color: S.blue },
    { label: "Scheduled Completion", value: formatDate(originalCompletionDate), color: S.subtext },
    { label: "Completion as per Latest EOT", value: latestExtDate ? formatDate(latestExtDate) : "No EOT yet", color: latestExtDate ? S.green : S.subtext },
    { label: "Total Days Delay", value: latestExtDate ? `+${totalDaysDelay} days` : "-", color: latestExtDate ? "#dc2626" : S.subtext },
  ];

  return (
    <div className="info-box-div-bg mt-3">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
        <h3 className="font-weight600 font-size-heading" style={{ margin: 0 }}>Project Timeline — EOT</h3>
        {isAdmin && (
          <button
            onClick={() => {
              if (allStagesCompleted) {
                toast.info("All workstages are completed for this project — no further EOT can be raised.");
                return;
              }
              setEditTarget(null);
              setShowAdd(true);
            }}
            disabled={allStagesCompleted}
            title={allStagesCompleted ? "All stages completed — EOT not applicable" : ""}
            style={{
              display: "flex", alignItems: "center", gap: "5px", padding: "6px 14px", borderRadius: "5px",
              border: `0.5px solid ${allStagesCompleted ? "#d1d5db" : S.blue}`,
              background: allStagesCompleted ? "#f3f4f6" : "#fff",
              color: allStagesCompleted ? "#9ca3af" : S.blue,
              fontSize: "12px", fontWeight: 500,
              cursor: allStagesCompleted ? "not-allowed" : "pointer",
            }}
          >
            + Add EOT
          </button>
        )}
      </div>
      {allStagesCompleted && (
        <div style={{ marginTop: "14px", padding: "12px 14px", background: "#f0fdf4", border: "0.5px solid #bbf7d0", borderRadius: S.radius, display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <span style={{ fontSize: "14px", flexShrink: 0 }}>✅</span>
          <p style={{ fontSize: "12px", fontWeight: 600, color: "#166534", margin: 0 }}>
            All workstages for this project are completed. No further EOT can be raised.
          </p>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginBottom: "16px" }}>
        {chips.map(({ label, value, color }) => (
          <div key={label} style={{ background: "#fff", border: S.border, borderRadius: S.radius, padding: "12px 14px" }}>
            <p style={{ fontSize: "10px", color: "#888", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
            <p style={{ fontSize: "13px", fontWeight: 600, color, margin: 0 }}>{value}</p>
          </div>
        ))}
      </div>

      <div style={{ border: S.border, borderRadius: S.radius, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr 130px 90px 110px 120px", background: S.bg, borderBottom: S.border, padding: "10px 14px", gap: "8px" }}>
          {["Sr.", "Project Code", "Project Name", "EOT Applied Date", "Extension Upto", "Status", "History", "Actions"].map((h) => (
            <span key={h} style={{ fontSize: "11px", fontWeight: 600, color: S.subtext, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: "32px", textAlign: "center" }}>
            <p style={{ fontSize: "13px", color: S.subtext, margin: 0 }}>Loading EOT records...</p>
          </div>
        ) : eots.length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center" }}>
            <p style={{ fontSize: "13px", color: S.subtext, margin: 0 }}>
              No EOT records yet.{isAdmin ? ' Click "+ Add EOT" to create one.' : ""}
            </p>
          </div>
        ) : (
          eots.map((eot, idx) => {
            const active = isActiveEOT(eot);
            return (
              <div
                key={eot.id}
                style={{ display: "grid", gridTemplateColumns: "50px 1fr 1fr 1fr 130px 90px 110px 120px", padding: "12px 14px", gap: "8px", borderBottom: idx < eots.length - 1 ? S.border : "none", alignItems: "center", background: active ? "#f0fdf4" : idx % 2 === 0 ? "#fff" : "#fafbfc" }}
              >
                <span style={{ fontSize: "12px", color: S.subtext, fontWeight: 500 }}>{idx + 1}.</span>
                <span style={{ fontSize: "12px", color: S.blue, fontWeight: 600 }}>{projectData.pr_code || "-"}</span>
                <span style={{ fontSize: "12px", color: S.text }}>{projectData.project_name || "-"}</span>
                <span style={{ fontSize: "12px", color: S.text }}>{formatDate(eot.applied_date)}</span>
                <span style={{ fontSize: "12px", color: active ? S.green : S.subtext, fontWeight: active ? 600 : 400 }}>{formatDate(eot.extension_upto_date)}</span>

                {/* ── Fix 2: Status badge in table ── */}
                <span style={{
                  fontSize: "10px", fontWeight: 700, padding: "3px 8px", borderRadius: "20px", textAlign: "center",
                  color: active ? S.green : "#6b7280",
                  background: active ? "#dcfce7" : "#f3f4f6",
                }}>
                  {active ? "Active" : "Superseded"}
                </span>

                <button onClick={() => setShowHistory(true)} style={{ padding: "4px 12px", borderRadius: "5px", border: S.border, background: "#fff", color: S.blue, fontSize: "11px", fontWeight: 500, cursor: "pointer" }}>
                  View History
                </button>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  {isAdmin && (
                    <button onClick={() => { setEditTarget(eot); setShowAdd(true); }} style={{ padding: "4px 10px", borderRadius: "5px", border: `0.5px solid ${S.blue}`, background: "#eff6ff", color: S.blue, fontSize: "11px", fontWeight: 500, cursor: "pointer" }}>
                      Edit
                    </button>
                  )}
                  {isSuperAdmin && (
                    <button onClick={() => setDeleteTarget(eot)} style={{ padding: "4px 10px", borderRadius: "5px", border: "0.5px solid #dc2626", background: "#fee2e2", color: "#dc2626", fontSize: "11px", fontWeight: 500, cursor: "pointer" }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {pendingWorkstages.length > 0 && (
        <div style={{ marginTop: "14px", padding: "12px 14px", background: "#fff7ed", border: "0.5px solid #fed7aa", borderRadius: S.radius, display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <span style={{ fontSize: "14px", flexShrink: 0 }}>⚠️</span>
          <div>
            <p style={{ fontSize: "12px", fontWeight: 600, color: "#92400e", margin: "0 0 4px" }}>
              {pendingWorkstages.length} pending / in-progress workstage{pendingWorkstages.length > 1 ? "s" : ""} will be included in the next EOT
            </p>
            <p style={{ fontSize: "11px", color: "#b45309", margin: 0 }}>
              {pendingWorkstages.map((ws) => `${ws.name} (${ws._eotStatus})`).join(" • ")}
            </p>
          </div>
        </div>
      )}

      <AddEOTModal
        show={showAdd}
        onHide={() => { setShowAdd(false); setEditTarget(null); }}
        onSave={handleSave}
        editData={editTarget}
        pendingWorkstages={pendingWorkstages}
        loaDate={loaDate}
        originalCompletionDate={originalCompletionDate}
        eotCount={eots.length}
        latestEOT={editTarget ? null : latestEOT}
      />
      <EOTHistoryModal show={showHistory} onHide={() => setShowHistory(false)} eots={eots} projectData={allData} />

      {/* ── Fix 1: pass full deleteTarget object, not just eot_no ── */}
      <DeleteConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        deleteTarget={deleteTarget}
      />
    </div>
  );
};

export { EOTTimeline };